/**
 * Country-specific roadmap (US / India), researched from trusted sources.
 * Shows only published (well-sourced) roadmaps; otherwise renders nothing and
 * the general roadmap below stays as the "typical" route.
 */
import { useEffect, useState } from "react";
import { BookOpen, GraduationCap, BadgeCheck, Briefcase, TrendingUp, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { COUNTRY_LABEL, type ContentCountry } from "@/lib/learnerCountry";
import usPack from "../../../public/data/country/us.json";

type Item = { text: string; sources: string[] };
type Sec = { expect: string; items: Item[] };
type Payload = { summary: string; school: Sec; education: Sec; credentials: Sec; first_job: Sec; growth: Sec };
type Source = { url: string; title?: string; domain: string };

const STEPS = [
  { key: "school", title: "At school", icon: BookOpen },
  { key: "education", title: "Courses and colleges", icon: GraduationCap },
  { key: "credentials", title: "Exams, licences and certifications", icon: BadgeCheck },
  { key: "first_job", title: "Your first job", icon: Briefcase },
  { key: "growth", title: "How you grow", icon: TrendingUp },
] as const;

const host = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };

export function CountryRoadmap({ ciId, country, audience }: { ciId: string; country: ContentCountry; audience?: "switcher" }) {
  const [row, setRow] = useState<{ payload: Payload; sources: Source[]; fetched_at: string } | null>(null);
  const [pay, setPay] = useState<{ median_wage: number | null; occupation: string | null; source_url: string | null; last_verified: string | null } | null>(null);

  useEffect(() => {
    let alive = true;
    setRow(null);
    setPay(null);
    supabase
      .from("career_country_market")
      .select("median_wage,occupation,source_url,last_verified")
      .eq("ci_id", ciId)
      .eq("country", country)
      .maybeSingle()
      .then(({ data }) => { if (alive) setPay(data as any); });
    supabase
      .from("career_roadmaps")
      .select("payload,sources,fetched_at")
      .eq("ci_id", ciId)
      .eq("country", country)
      .eq("status", "published")
      .order("fetched_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => { if (alive && data) setRow(data as any); });
    return () => { alive = false; };
  }, [ciId, country]);

  const payLine = country === "US" ? (
    <p className="rounded-2xl border border-border bg-card px-4 py-3 text-sm">
      <span className="font-medium">Typical US pay: </span>
      {pay?.median_wage ? (
        <>
          ${Math.round(pay.median_wage).toLocaleString("en-US")} a year (median{pay.occupation ? `, ${pay.occupation}` : ""}).{" "}
          {pay.source_url && <a href={pay.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{pay.last_verified ?? "Source"}<span className="sr-only"> (opens in a new tab)</span></a>}
        </>
      ) : <span className="text-muted-foreground">not yet verified</span>}
    </p>
  ) : null;

  if (!row) {
    return (
      <div className="space-y-3">
        {payLine}
        <p className="rounded-3xl border border-dashed border-border p-5 text-sm text-muted-foreground">
          We're still researching the {COUNTRY_LABEL[country]} route for this role. {audience === "switcher" ? "Use the transition plan above to explore; confirm qualifications and licences with official providers." : "Until then, the typical route below is a good guide."}
        </p>
      </div>
    );
  }
  const p = row.payload;
  return (
    <div className="space-y-3">
      {payLine}
      {p.summary && <p className="text-[15px] leading-relaxed text-foreground/90">{p.summary}</p>}
      <ol className="space-y-3">
        {STEPS.filter(step => audience !== "switcher" || step.key !== "school").map(({ key, title, icon: Icon }, i) => {
          const sec = p[key];
          if (!sec?.items?.length) return null;
          return (
            <li key={key} className="rounded-3xl border border-border bg-card p-5 md:p-6">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-primary">
                <Icon className="h-4 w-4" aria-hidden /> Step {i + 1}
              </div>
              <h3 className="mt-1 font-display text-lg font-medium">{audience === "switcher" && key === "first_job" ? "Entry routes into the field" : title}</h3>
              {sec.expect && <p className="mt-1 text-sm text-muted-foreground"><span className="font-medium text-foreground">What to expect:</span> {sec.expect}</p>}
              <ul className="mt-3 space-y-2.5">
                {sec.items.map((it) => (
                  <li key={it.text} className="text-[15px] leading-relaxed text-foreground/90">
                    {it.text}{" "}
                    {it.sources.slice(0, 2).map((u) => (
                      <a key={u} href={u} target="_blank" rel="noopener noreferrer"
                        className="ml-1 inline-flex items-center gap-0.5 text-xs text-primary underline-offset-2 hover:underline">
                        {host(u)}<ExternalLink className="h-3 w-3" aria-hidden /><span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    ))}
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-muted-foreground">
        Sources: {[...new Set(row.sources.map((s) => s.domain))].join(", ")} · checked{" "}
        {new Date(row.fetched_at).toLocaleDateString(country === "US" ? "en-US" : "en-IN", { day: "numeric", month: "short", year: "numeric" })}
      </p>
    </div>
  );
}

type PackRow = Record<string, string> & { _name: string };
const pack = (usPack as any).kinds as Record<string, PackRow[]>;

/** US colleges, exams and aid from the US Country Intelligence Pack. */
export function USResources() {
  const groups = [
    { title: "Colleges and universities", rows: pack.university ?? [], sub: (r: PackRow) => [r.City, r.Type].filter(Boolean).join(" · ") },
    { title: "Admissions and licensing exams", rows: pack.exam ?? [], sub: (r: PackRow) => [r["Conducting Body"], r.Purpose].filter(Boolean).join(" · ") },
    { title: "Financial aid and scholarships", rows: pack.scholarship ?? [], sub: (r: PackRow) => [r.Provider, r.Type].filter(Boolean).join(" · ") },
  ];
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {groups.map((g) => (
        <div key={g.title} className="rounded-3xl border border-border bg-card p-5">
          <h3 className="font-display text-base font-medium">{g.title}</h3>
          <ul className="mt-3 space-y-2">
            {g.rows.slice(0, 8).map((r) => (
              <li key={r["Unique ID"]} className="text-sm">
                {r["Primary Source URL"] ? (
                  <a href={r["Primary Source URL"]} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary hover:underline">{r._name}</a>
                ) : <span>{r._name}</span>}
                {g.sub(r) && <span className="block text-xs text-muted-foreground">{g.sub(r)}</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="md:col-span-3 text-xs text-muted-foreground">
        From the US Country Intelligence Pack. These are starting points; check details with each provider.
      </p>
    </div>
  );
}
